/**
 * Copyright (C) 2023 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import { Box } from '@mui/material';
import { Feature, FeatureCollection, GeoJsonProperties, MultiPolygon, Polygon } from 'geojson';
import { isNil, size } from 'lodash';
import isEmpty from 'lodash/isEmpty';
import { useEffect, useMemo, useState } from 'react';
import { connect, shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useParams, useRouteMatch } from 'react-router-dom';
import { InstallationType, NetworkGroup, TerminalDetails } from '../../../shared/entities/terminal';
import { RootState, selectIsRequestingPostServiceTierRequest } from '../../../store';
import { switchOverlaysContext } from '../../../store/actions';
import { requestAntennas } from '../../../store/antennas/actions';
import { requestBeams } from '../../../store/beams/actions';
import {
  FTV_MAP_ID,
  addBeamToMap,
  clearCurrentOverlaysContextExceptTailTagOptions,
  removeBeamFromMap,
  setCurrentBeams,
  setCurrentNextBeamsChecked,
  setFlightPathBeamsChecked,
  setFlightPathBeamsDisabled,
  setFlightPathWaypointsChecked,
  setFlightPathWaypointsDisabled,
  setMapHidden,
  setMapSettingsFTVMapSize,
  setNextBeam,
  setSelectedAntennaTypeId,
} from '../../../store/maps/actions';
import { BeamOnMap, OverlaysContextsEnum } from '../../../store/maps/reducer';
import { setAnnotations } from '../../../store/terminalHistory/actions';
import { cancelRequestTailDetailsPanelTerminal, getTailDetailsPanelTerminal } from '../../../store/terminals/actions';
import {
  clearSelectedDatapathUuidInPlatformList,
  clearSelectedTerminalTabIndex,
  hideTerminalsInYourBeamPanel,
  openDetailsPanel,
  setPrintMode,
  setSelectedDatapathUuidInPlatformList,
  setSelectedTerminalTabIndex,
  unsetPrintMode,
} from '../../../store/ui/actions';
import { getArcLightMapVersion, getKaBeamOverlayName, getViHnaMapVersion } from '../../../utils/BeamUtils';
import { isOnline } from '../../../utils/ConnectionStatusUtils';
import { FEATURE_FLAGS, requireFeatureFlag } from '../../../utils/FeatureFlagUtils';
import {
  KA_TAB,
  KU_TAB,
  OVERVIEW_TAB,
  getKaTerminalOnAircraft,
  getKuTerminalOnAircraft,
  getOtherTerminalOnAircraft,
  getSelectedTabBasedOnTerminalTypeLabel,
  getSelectedTerminalTypeLabelBasedOnTab,
  getTerminalDisplayName,
} from '../../../utils/TerminalUtilities';
import ErrorBoundary from '../../common/layout/ErrorBoundary';
import ModalLoadingSpinner from '../../common/layout/ModalLoadingSpinner';
import { MenuItems } from '../../common/layout/menu/MenuItems';
import { GOOGLE_MAP_INSTANCE_KEY } from '../../common/map/GoogleMapSingleton';
import { TerminalsInYourBeamPanelContainer } from '../../common/map_overlays/TerminalsInYourBeamPanelContainer';
import { BEAMS_NETWORK_GROUP_IDS } from '../../common/map_overlays/overlaysDefn';
import { MapSizesEnum, getMapHeight } from '../../common/map_settings/MapSizeController';
import { useHistory } from 'react-router-dom';
import ModalDialog from '../../common/dialogs/ModalDialog';
import TailDetailsPanel, { TAIL_DETAILS_PANEL_WIDTH } from '../TailDetailsPanel/TailDetailsPanel';
import ChartsContainer from './charts/ChartsContainer';
import { MapType, Network } from '../../../shared/entities/beam';

interface FullTailViewContainerProps extends DispatchProps, StateProps {}

// Derives the current beam, next beam, and antenna/map file version values from the selected terminal and existing beams
function getInitialBeamValues(
  selectedTerminal: TerminalDetails,
  beams: FeatureCollection<MultiPolygon | Polygon, GeoJsonProperties> | undefined,
  viasatBeam: string,
): {
  currBeamsNew: BeamOnMap[];
  nextBeamNew: BeamOnMap;
  antennaTypeIdNew: number;
  sscfFileVerNew: string;
  viHnaPolicyVerNew: string;
} {
  const { antennaTypeId, beamName, nextBeam, networkOrigin, sscfFileVer, viHnaPolicyVersion } = selectedTerminal;
  let currBeamsNew: BeamOnMap[] = [
    {
      beamId: beamName,
      networkType: beams?.features?.find(
        (feature: Feature<MultiPolygon | Polygon>) => feature.properties?.name === beamName,
      )?.properties?.network,
      mapType: beams?.features?.find(
        (feature: Feature<MultiPolygon | Polygon>) => feature.properties?.name === beamName,
      )?.properties?.mapType,
    },
  ];
  let nextBeamNew: BeamOnMap = {
    beamId: nextBeam,
    networkType: beams?.features?.find(
      (feature: Feature<MultiPolygon | Polygon>) => feature.properties?.name === nextBeam,
    )?.properties?.network,
    mapType: beams?.features?.find((feature: Feature<MultiPolygon | Polygon>) => feature.properties?.name === nextBeam)
      ?.properties?.mapType,
  };
  // TODO: temporary condition to show Ka beams outline since Ka beams are spot-beams
  if (networkOrigin && networkOrigin.toLowerCase() === 'viasat') {
    currBeamsNew = [
      {
        beamId: getKaBeamOverlayName(selectedTerminal, viasatBeam),
        networkType: Network.VIASAT,
        mapType: MapType.COVERAGE,
      },
    ];
  } else if (networkOrigin && networkOrigin.toLowerCase() === 'viasat-3') {
    currBeamsNew = [
      {
        beamId: getKaBeamOverlayName(selectedTerminal, viasatBeam),
        networkType: Network.VIASAT_3,
        mapType: MapType.COVERAGE,
      },
    ];
  }

  return {
    currBeamsNew,
    nextBeamNew,
    antennaTypeIdNew: antennaTypeId,
    sscfFileVerNew: sscfFileVer,
    viHnaPolicyVerNew: viHnaPolicyVersion,
  };
}

// Derives the current beams, next beam, and antenna/map file version values for a KU+KA terminal from the selected terminal
function getKuPlusKaBeams(
  selectedTerminal: TerminalDetails,
  selectedTerminalTabIndex: number | undefined,
  viasatBeam: string,
): {
  currBeamsNew: BeamOnMap[];
  nextBeamNew: BeamOnMap;
  antennaTypeIdNew: number;
  sscfFileVerNew: string;
  viHnaPolicyVerNew: string;
} {
  const tabIndex = Math.max(selectedTerminalTabIndex || OVERVIEW_TAB, 0);
  const kuTerminal = getKuTerminalOnAircraft(selectedTerminal);
  const kaTerminal = getKaTerminalOnAircraft(selectedTerminal);
  const onOverviewOrKuTab = tabIndex === OVERVIEW_TAB || tabIndex === KU_TAB;
  const onOverviewOrKaTab = tabIndex === OVERVIEW_TAB || tabIndex === KA_TAB;
  const nextBeamNew = onOverviewOrKuTab
    ? { beamId: kuTerminal.nextBeam, networkType: Network.VGNET, mapType: MapType.COVERAGE }
    : { beamId: '', networkType: '', mapType: '' };
  const currBeamsNew = [];
  if (isOnline(kaTerminal.connectivityStatus) && onOverviewOrKaTab) {
    currBeamsNew.push({
      beamId: getKaBeamOverlayName(kaTerminal, viasatBeam),
      networkType: Network.VIASAT,
      mapType: MapType.COVERAGE,
    });
  }
  if (isOnline(kuTerminal.connectivityStatus) && onOverviewOrKuTab) {
    currBeamsNew.push({ beamId: kuTerminal.beamName, networkType: Network.VGNET, mapType: MapType.COVERAGE });
  }

  return {
    currBeamsNew,
    nextBeamNew,
    antennaTypeIdNew: kuTerminal.antennaTypeId,
    sscfFileVerNew: kuTerminal.sscfFileVer,
    viHnaPolicyVerNew: kaTerminal.viHnaPolicyVersion,
  };
}

function FullTailViewContainer({
  setSelectedTerminal,
  getTailDetailsPanelTerminal,
  cancelRequestTailDetailsPanelTerminal,
  switchOverlaysContext,
  setFlightPathBeamsDisabled,
  setFlightPathBeamsChecked,
  setFlightPathWaypointsDisabled,
  setFlightPathWaypointsChecked,
  selectedOverlaidFlightLegId,
  mapSettingsFtvMapHeight,
  setMapSettingsFTVMapSize,
  antennas,
  requestAntennas,
  clearCurrentOverlaysContext,
  setAnnotations,
  currentNextBeamsChecked,
  addBeamToMap,
  removeBeamFromMap,
  tailDetailsPanelTerminal,
  requestBeamsForTerminal,
  setSelectedAntennaTypeId,
  specialBeamsNetworkMapping,
  beams,
  nextBeam,
  setNextBeam,
  currentBeams,
  setCurrentBeams,
  selectedAntennaId,
  setPrintMode,
  unsetPrintMode,
  fleetViewHiddenTerminals,
  selectedTerminalTabIndex,
  setSelectedTerminalTabIndex,
  clearSelectedTerminalTabIndex,
  hideTerminalsInYourBeamPanel,
}: FullTailViewContainerProps) {
  const [viasatBeam, setViasatBeam] = useState('');
  // this is needed to ensure that the antenna id does not reset the ui state of
  // the overlay when the antenna's are the same between new terminal requests
  const [antennaId, setAntennaId] = useState(selectedAntennaId);
  const [sscfFileVer, setSscfFileVer] = useState('');
  const [viHnaPolicyVersion, setViHnaPolicyVer] = useState('');
  const [supportsFtv, setSupportsFtv] = useState(true);
  const { datapathUuid } = useParams();
  const dispatch = useDispatch();
  const isExportView = useRouteMatch(`/${MenuItems.fleet}/:datapathUuid/export`);
  const chartsContainerLeftMargin = TAIL_DETAILS_PANEL_WIDTH;
  const isRequestingPostServiceTierRequest = useSelector(selectIsRequestingPostServiceTierRequest, shallowEqual);
  const history = useHistory();

  // Return to fleet view
  const acceptReturnToFleetViewHandler = () => {
    history.push(`/${MenuItems.fleet}`);
  };

  // Check if Linkway terminal is detected, if so, prompt user to fall back to fleet view
  useEffect(() => {
    if (tailDetailsPanelTerminal?.networkGroup === NetworkGroup.LINKWAY) {
      setSupportsFtv(false);
    }
  }, [tailDetailsPanelTerminal]);

  // Ensure the map is shown and the context is updated
  useEffect(() => {
    switchOverlaysContext(OverlaysContextsEnum.FTV);
  }, [switchOverlaysContext]);

  useEffect(() => {
    dispatch(setMapHidden(FTV_MAP_ID)(false));

    return () => {
      dispatch(setMapHidden(FTV_MAP_ID)(true));
    };
  }, [dispatch]);

  useEffect(() => {
    // Set map height the first time to default medium size based on screen resolution
    if (!mapSettingsFtvMapHeight) {
      setMapSettingsFTVMapSize(MapSizesEnum.MEDIUM, getMapHeight(MapSizesEnum.MEDIUM));
    }
  }, [mapSettingsFtvMapHeight, setMapSettingsFTVMapSize]);

  // set the correct terminal for the FTV
  useEffect(() => {
    setSelectedTerminal(datapathUuid);
    cancelRequestTailDetailsPanelTerminal();
    getTailDetailsPanelTerminal(datapathUuid);

    // clear the current context when switching between terminals
    // and unmounting
    return () => {
      clearCurrentOverlaysContext();
      setAnnotations([]);
      // Reset the tab index so that the next FTV open doesn't open the tab opened in a previous FTV session.
      clearSelectedTerminalTabIndex();
    };
  }, [
    datapathUuid,
    setSelectedTerminal,
    cancelRequestTailDetailsPanelTerminal,
    getTailDetailsPanelTerminal,
    clearCurrentOverlaysContext,
    setAnnotations,
    clearSelectedTerminalTabIndex,
  ]);

  //request the antennas
  useEffect(() => {
    // Request antennas if the store does not have antennas yet.
    // Antennas could be present due to a previous call to requestAntennas performed in Fleet View
    if (isNil(antennas) || isEmpty(antennas)) {
      requestAntennas();
    }
  }, [antennas, requestAntennas]);

  // On initial FTV load for a dual-modem terminal, set the tab to match the modem that was selected, as it is initally set to undefined.
  useEffect(() => {
    if (
      !isNil(tailDetailsPanelTerminal) &&
      isNil(selectedTerminalTabIndex) &&
      tailDetailsPanelTerminal.installationType === InstallationType.DUAL_TERMINAL
    ) {
      const tabIndex = getSelectedTabBasedOnTerminalTypeLabel(tailDetailsPanelTerminal);
      setSelectedTerminalTabIndex(tabIndex);
    }
  }, [tailDetailsPanelTerminal, datapathUuid, selectedTerminalTabIndex, setSelectedTerminalTabIndex]);

  // set the flight path beams to be turned on when a flight leg is selected
  // for a mission
  useEffect(() => {
    if (!isNil(selectedOverlaidFlightLegId)) {
      setFlightPathBeamsDisabled(false);
      setFlightPathBeamsChecked(true);
      setFlightPathWaypointsDisabled(false);
      setFlightPathWaypointsChecked(false);
    }
  }, [
    selectedOverlaidFlightLegId,
    setFlightPathBeamsDisabled,
    setFlightPathBeamsChecked,
    setFlightPathWaypointsDisabled,
    setFlightPathWaypointsChecked,
  ]);

  // get the viasat beam name from the special mapping
  useEffect(() => {
    const setViasatBeamForNetworkGroup = (networkGroup: BEAMS_NETWORK_GROUP_IDS) => {
      setViasatBeam(
        specialBeamsNetworkMapping[networkGroup].find(element =>
          ((beams || {}).features || []).find(
            (feature: Feature<Polygon | MultiPolygon>) => (feature.properties || {}).name === element,
          ),
        ) || '',
      );
    };

    if (!isNil(specialBeamsNetworkMapping) && !isNil(specialBeamsNetworkMapping[BEAMS_NETWORK_GROUP_IDS.Ka])) {
      setViasatBeamForNetworkGroup(BEAMS_NETWORK_GROUP_IDS.Ka);
    }

    // VS3 is separate from the rest of Viasat Internet for now
    if (!isNil(specialBeamsNetworkMapping) && !isNil(specialBeamsNetworkMapping[BEAMS_NETWORK_GROUP_IDS.VS3])) {
      setViasatBeamForNetworkGroup(BEAMS_NETWORK_GROUP_IDS.VS3);
    }
  }, [beams, specialBeamsNetworkMapping]);

  useEffect(() => {
    // Update the antenna redux store when the local antenna or tabs change
    if (antennaId) {
      setSelectedAntennaTypeId(antennaId);
    }
  }, [selectedTerminalTabIndex, antennaId, setSelectedAntennaTypeId]);

  const terminalNetworkGroup = useMemo(() => {
    return tailDetailsPanelTerminal?.networkGroup;
  }, [tailDetailsPanelTerminal]);

  useEffect(() => {
    // Request the beams for the terminal when the antenna or mapfile versions change
    if (terminalNetworkGroup === NetworkGroup.GX) {
      requestBeamsForTerminal('gx');
    } else if (antennaId && sscfFileVer) {
      requestBeamsForTerminal(getArcLightMapVersion(antennaId, sscfFileVer), getViHnaMapVersion(viHnaPolicyVersion));
    }
  }, [antennaId, antennas, sscfFileVer, viHnaPolicyVersion, requestBeamsForTerminal, terminalNetworkGroup]);

  // update the current beam, next beam, antenna id, and mapfile versions from the terminal information
  useEffect(() => {
    // if terminal is dual terminal, update the selected terminal based on tab selection
    const selectedTerminalType = getSelectedTerminalTypeLabelBasedOnTab(
      selectedTerminalTabIndex,
      tailDetailsPanelTerminal,
    );
    const terminalTypeLabel = (tailDetailsPanelTerminal || {}).terminalTypeLabel;
    const selectedTerminal =
      (tailDetailsPanelTerminal || {}).installationType === InstallationType.DUAL_TERMINAL &&
      selectedTerminalType !== terminalTypeLabel
        ? getOtherTerminalOnAircraft(tailDetailsPanelTerminal)
        : tailDetailsPanelTerminal;
    // Allow the beam update when:
    // 1. The selected terminal is the one we opened FTV for
    // 2. It is a DUAL_TERMINAL and the user switched to the other modem's tab.
    //    selectedTerminal is the peer terminal (different datapathUuid), so check that the
    //    loaded terminal still matches the datapathUuid.
    const isSelectedTerminal = datapathUuid === selectedTerminal?.datapathUuid;
    const isOtherModemOnSameAircraft =
      tailDetailsPanelTerminal?.installationType === InstallationType.DUAL_TERMINAL &&
      datapathUuid === tailDetailsPanelTerminal?.datapathUuid;

    if (!isNil(selectedTerminal) && (isSelectedTerminal || isOtherModemOnSameAircraft)) {
      const { currBeamsNew, nextBeamNew, antennaTypeIdNew, sscfFileVerNew, viHnaPolicyVerNew } =
        selectedTerminal.installationType === InstallationType.KU_PLUS_KA
          ? getKuPlusKaBeams(selectedTerminal, selectedTerminalTabIndex, viasatBeam)
          : getInitialBeamValues(selectedTerminal, beams, viasatBeam);

      setCurrentBeams(currBeamsNew);
      setNextBeam(nextBeamNew);
      setAntennaId(antennaTypeIdNew);
      setSscfFileVer(sscfFileVerNew);
      setViHnaPolicyVer(viHnaPolicyVerNew);
    }
  }, [
    tailDetailsPanelTerminal,
    datapathUuid,
    requestBeamsForTerminal,
    selectedTerminalTabIndex,
    viasatBeam,
    setNextBeam,
    setCurrentBeams,
    beams,
  ]);

  // add the current and next beam to map when changed
  useEffect(() => {
    if (!isNil(currentBeams) && size(currentBeams) && currentNextBeamsChecked) {
      currentBeams.forEach((beam: BeamOnMap) => {
        addBeamToMap(beam.beamId, beam.networkType, beam.mapType);
      });
    }
    return () => {
      if (!isNil(currentBeams)) {
        currentBeams.forEach((beam: BeamOnMap) => {
          removeBeamFromMap(beam.beamId, beam.networkType, beam.mapType);
        });
      }
    };
  }, [currentBeams, currentNextBeamsChecked, addBeamToMap, removeBeamFromMap]);
  useEffect(() => {
    if (!isNil(nextBeam) && currentNextBeamsChecked) {
      addBeamToMap(nextBeam.beamId, nextBeam.networkType, nextBeam.mapType);
    }
    return () => {
      if (!isNil(nextBeam)) {
        removeBeamFromMap(nextBeam.beamId, nextBeam.networkType, nextBeam.mapType);
      }
    };
  }, [nextBeam, currentNextBeamsChecked, addBeamToMap, removeBeamFromMap]);

  // set up the title for the export view
  useEffect(() => {
    if (isExportView && tailDetailsPanelTerminal) {
      const title = `Full Tail Report ${getTerminalDisplayName(tailDetailsPanelTerminal)}`;

      if (document.title !== title) {
        document.title = title;
      }
    }

    return () => {
      document.title = `Viasat Unified Portal`;
    };
  }, [isExportView, tailDetailsPanelTerminal]);

  // set print mode when on the export view
  useEffect(() => {
    if (isExportView) {
      setPrintMode();
    }

    return () => {
      unsetPrintMode();
    };
  }, [setPrintMode, unsetPrintMode, isExportView]);

  // When the FTV unmounts, if the selected terminal is not hidden on the Fleet View, open the
  // Details Panel so it is visible on the Fleet View when the user navigates there.
  //
  // If the FTV terminal is hidden on the Fleet View, clear the selected terminal id so the Panel
  // is not visible and the hidden terminal is not selected on the Fleet View
  useEffect(() => {
    return () => {
      if (fleetViewHiddenTerminals.some(hiddenTerminal => hiddenTerminal === datapathUuid)) {
        dispatch(clearSelectedDatapathUuidInPlatformList());
      } else {
        dispatch(openDetailsPanel());
      }
    };
  }, [fleetViewHiddenTerminals, dispatch, datapathUuid]);

  return (
    <div>
      <ErrorBoundary message="Unable to render terminal details panel">
        {!isExportView && <TailDetailsPanel isFullTailViewMode={true} />}
      </ErrorBoundary>
      <ErrorBoundary message="Unable to render the charts">
        <ChartsContainer leftMargin={chartsContainerLeftMargin} isExportView={isExportView} supportsFtv={supportsFtv} />
      </ErrorBoundary>
      <ErrorBoundary
        message="Unable to render the terminals in your beam panel"
        onReset={() => {
          hideTerminalsInYourBeamPanel();
        }}
      >
        <TerminalsInYourBeamPanelContainer />
        {isRequestingPostServiceTierRequest ? (
          <Box sx={{ position: 'absolute', width: '100vw', height: '100vh', zIndex: 9999 }}>
            <ModalLoadingSpinner></ModalLoadingSpinner>
          </Box>
        ) : (
          <></>
        )}
      </ErrorBoundary>
      <ErrorBoundary message="Unable to render pop-up dialog">
        <ModalDialog
          open={!supportsFtv}
          hideLeftBtn={true}
          leftBtnText=""
          rightBtnText="OK"
          title="Terminal View Unavailable"
          message={'Full terminal view is unavailable for this terminal. You will be \n redirected to Fleet View.'}
          onLeftBtnClicked={() => null}
          onRightBtnClicked={() => acceptReturnToFleetViewHandler()}
        />
      </ErrorBoundary>
    </div>
  );
}

const mapStateToProps = (state: RootState) => ({
  specialBeamsNetworkMapping: state.config.appConfig.specialBeamsNetworkMapping,
  selectedOverlaidFlightLegId:
    state.flightLegs.mapContext[GOOGLE_MAP_INSTANCE_KEY]?.uiState[state.flightLegs.currentContext]
      ?.selectedOverlaidFlightLegId,
  antennas: state.antennas.antennas,
  tailDetailsPanelTerminal: state.terminals.tailDetailsPanelTerminal,
  selectedTerminalTabIndex: state.ui.overlaysContextUiState[state.ui.overlaysCurrentContext].selectedTerminalTabIndex,
  currentNextBeamsChecked:
    state.maps.mapContext[GOOGLE_MAP_INSTANCE_KEY]?.overlaysUiState[state.maps.overlaysCurrentContext]
      ?.currentNextBeamsChecked,
  currentBeams:
    state.maps.mapContext[GOOGLE_MAP_INSTANCE_KEY]?.overlaysUiState[state.maps.overlaysCurrentContext]?.currentBeams,
  nextBeam:
    state.maps.mapContext[GOOGLE_MAP_INSTANCE_KEY]?.overlaysUiState[state.maps.overlaysCurrentContext]?.nextBeam,
  selectedAntennaId:
    state.maps.mapContext[GOOGLE_MAP_INSTANCE_KEY]?.overlaysUiState[state.maps.overlaysCurrentContext]
      ?.selectedAntennaTypeId,
  mapSettingsFtvMapHeight: state.maps.mapContext[GOOGLE_MAP_INSTANCE_KEY]?.mapSettingsFtvMapHeight,
  beams: state.beams.beams,
  fleetViewHiddenTerminals: state.ui.overlaysContextUiState[state.ui.overlaysCurrentContext].hiddenTerminalsList,
});
type StateProps = ReturnType<typeof mapStateToProps>;

const mapDispatchToProps = {
  setSelectedTerminal: setSelectedDatapathUuidInPlatformList,
  setSelectedTerminalTabIndex,
  clearSelectedTerminalTabIndex,
  requestAntennas,
  getTailDetailsPanelTerminal,
  cancelRequestTailDetailsPanelTerminal,
  requestBeamsForTerminal: requestBeams,
  switchOverlaysContext,
  clearCurrentOverlaysContext: clearCurrentOverlaysContextExceptTailTagOptions(FTV_MAP_ID),
  setAnnotations,
  setSelectedAntennaTypeId: setSelectedAntennaTypeId(FTV_MAP_ID),
  addBeamToMap: addBeamToMap(FTV_MAP_ID),
  removeBeamFromMap: removeBeamFromMap(FTV_MAP_ID),
  setCurrentBeams: setCurrentBeams(FTV_MAP_ID),
  setNextBeam: setNextBeam(FTV_MAP_ID),
  setFlightPathBeamsDisabled: setFlightPathBeamsDisabled(FTV_MAP_ID),
  setFlightPathBeamsChecked: setFlightPathBeamsChecked(FTV_MAP_ID),
  setFlightPathWaypointsDisabled: setFlightPathWaypointsDisabled(FTV_MAP_ID),
  setFlightPathWaypointsChecked: setFlightPathWaypointsChecked(FTV_MAP_ID),
  setCurrentNextBeamsChecked: setCurrentNextBeamsChecked(FTV_MAP_ID),
  setMapSettingsFTVMapSize: setMapSettingsFTVMapSize(FTV_MAP_ID),
  setPrintMode,
  unsetPrintMode,
  clearSelectedDatapathUuidInPlatformList,
  hideTerminalsInYourBeamPanel,
};

type DispatchProps = typeof mapDispatchToProps;

export default connect(mapStateToProps, mapDispatchToProps)(FullTailViewContainer);

export const FullTailViewContainerWithFeatureFlag = requireFeatureFlag(
  connect(mapStateToProps, mapDispatchToProps)(FullTailViewContainer),
  FEATURE_FLAGS.isFullTailViewEnabled,
  `/${MenuItems.fleet}`,
  {},
);
